import {
  ToggleGroup,
  ToggleGroupItem,
  type ToggleGroupKey
} from "@design-lab/shared/ui/toggleGroup";
import {
  usePlacementNavigation,
  usePlacementPosition,
  useArtworkForm
} from "@design-lab/entities/artwork";
import { trackPlacementChanged } from "@design-lab/shared/lib/analytics";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { useCurrentComposition } from "@design-lab/entities/composition";

export function ImagePlacement() {
  const { lens } = useArtworkForm();
  const currentPlacementLens = lens.focus("currentPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const { commitPlacement } = usePlacementNavigation();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};
  const placementOptions: readonly PlacementFormPosition[] = ["front", "frontLeftChest", "back"];

  const options =
    currentPlacement.placement === "back"
      ? [{ value: "back", label: "Back center", disabled: false }]
      : [
          {
            value: "front",
            label: "Front center",
            disabled: !sides?.front?.includes("front")
          },
          {
            value: "frontLeftChest",
            label: "Left chest",
            disabled: !sides?.front?.includes("front_left_chest")
          }
        ];

  const handlePlacementChange = (keys: Set<ToggleGroupKey>) => {
    const [first] = keys;
    if (!first) return;

    const nextPlacement = String(first);
    if (!placementOptions.includes(nextPlacement as PlacementFormPosition)) return;

    const toPlacement = nextPlacement as PlacementFormPosition;
    const fromPlacement = currentPlacement.placement;

    if (fromPlacement !== toPlacement) {
      trackPlacementChanged(fromPlacement, toPlacement);
    }

    // Why: single-commit consistent write (MN-9145) — replaces the manual
    // string-prefix-gated selectedFrontPlacement + currentPlacement pair and
    // also keeps designSubTabAtom in sync with the chosen placement.
    commitPlacement(toPlacement);
  };

  return (
    <ToggleGroup
      variant="outline"
      size="lg"
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[currentPlacement.placement]}
      onSelectionChange={handlePlacementChange}
      className="justify-start lg:justify-center"
    >
      {options.map(option => (
        <ToggleGroupItem
          isDisabled={option.disabled}
          data-testid={`placement-${option.value}-toggle`}
          key={option.value}
          id={option.value}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
